import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { DataGridLeafColumn } from '@ibsys/kit';
import type { QualityColumnDescriptor, QualityDetailItem } from '@/entities/dashboards';
import type { ConversationAttributeItem } from '@/entities/attributes';
import { withSecondsSuffix } from '@/entities/attributes';
import { buildConversationUrl } from '@/entities/conversations';
import { renderQualityAttributeValue } from '@/entities/conversations/model/utils';
import { useTimeDisplayFormat } from '@/entities/settings';
import { formatHumanDate } from '@/shared/lib/utils/date';
import { formatTimeInterval, withSecondsLabel } from '@/shared/lib/utils/string';
import { CopyButton } from '@/shared/ui';
import { getBlockResult, getQualityResultText, getTotalResult, scoreClass, totalScoreClass } from './utils';

/** Обязательные main-колонки таблицы (всегда видимы и идут первыми) */
export const MANDATORY_COLUMN_IDS = ['copyLink', 'date', 'consultant', 'totalScore'] as const;

/** Префикс id у динамических колонок «балл блока» (id блока следует после префикса). */
export const BLOCK_COLUMN_PREFIX = 'block_';

/**
 * Коды основных колонок, значения которых разговор отдаёт полями своей записи: время начала,
 * время завершения и длительность. Атрибутными колонками эти свойства не приходят, поэтому вид
 * их значений задан здесь, а не общим правилом атрибутов. По умолчанию колонки выключены.
 */
export const RECORD_FIELD_COLUMN_IDS = ['startedAt', 'finishedAt', 'duration'] as const;

type Params = {
  blockNames: string[];
  availableColumns: QualityColumnDescriptor[];
  availableAttributes: ConversationAttributeItem[];
  blockIdByName: Map<string, number>;
  t: TFunction<'qualityDashboard'>;
};

/**
 * Строит полный набор колонок таблицы «Все детали»: обязательные основные +
 * дополнительные main/attribute/expertReport из availableColumns + по столбцу
 * с баллом на каждый критерий выборки.
 */
export const useAllDetailsColumns = ({
  blockNames,
  availableColumns,
  availableAttributes,
  blockIdByName,
  t,
}: Params): DataGridLeafColumn<QualityDetailItem>[] => {
  const { t: tCommon, i18n } = useTranslation('common');
  const timeDisplayFormat = useTimeDisplayFormat();

  return useMemo(() => {
    const mandatory: DataGridLeafColumn<QualityDetailItem>[] = [
      {
        id: 'copyLink',
        header: '',
        accessor: 'conversationId',
        size: 48,
        align: 'center',
        enableResizing: false,
        enableReorder: false,
        cell: ({ row }) => (
          <CopyButton text={buildConversationUrl(row.conversationId)} tooltip={t('allDetails.copyLink')} />
        ),
      },
      {
        id: 'date',
        header: t('allDetails.columns.date'),
        accessor: 'evaluationDate',
        size: 120,
        cell: ({ value }) => (value as string | null) ?? null,
        enableSorting: true,
      },
      {
        id: 'consultant',
        header: t('allDetails.columns.consultant'),
        accessor: 'consultantName',
        size: 180,
        cell: ({ value }) => (value as string) || null,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'text' },
      },
      {
        id: 'totalScore',
        header: t('allDetails.columns.totalScore'),
        // Итог показывается таким, каким его дало правило расчёта: балл — баллом, значение —
        // значением, набор значений — перечислением
        accessor: (row) => getQualityResultText(getTotalResult(row)),
        size: 110,
        align: 'center',
        className: 'font-bold',
        // Зона качества есть только у балла: итог-значение и итог-набор долей от максимума
        // не измеряются
        cellClassName: ({ row }) => totalScoreClass(row.totalRating),
        cell: ({ row }) => getQualityResultText(getTotalResult(row)),
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'integer' },
      },
    ];

    // Дополнительные main-колонки (client, externalId и т.п.) из availableColumns,
    // не входящие в обязательные. Если в availableColumns ничего нет — используем
    // дефолтный набор клиента и обращения, чтобы таблица не теряла привычные колонки.
    const fallbackMain: DataGridLeafColumn<QualityDetailItem>[] = [
      {
        id: 'clientName',
        header: t('allDetails.columns.client'),
        accessor: 'clientName',
        size: 180,
        cell: ({ value }) => (value as string) || null,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'text' },
      },
      {
        id: 'externalId',
        header: t('allDetails.columns.externalId'),
        accessor: 'externalId',
        size: 120,
        cell: ({ value }) => (value as string) || null,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'text' },
      },
    ];

    const mainFromApi = availableColumns
      .filter((c) => c.type === 'main' && !MANDATORY_COLUMN_IDS.includes(c.code as never))
      .filter((c) => c.code !== 'evaluationDate' && c.code !== 'consultantName' && c.code !== 'totalRating')
      .map((c): DataGridLeafColumn<QualityDetailItem> => {
        // clientName и externalId — поддерживают серверную сортировку и фильтр.
        const isSortableMain = c.code === 'clientName' || c.code === 'externalId';
        // Длительность и моменты разговора приходят полями записи, а не готовыми к показу строками
        // атрибута: интервал показывается в действующем формате, момент — местным написанием даты.
        const isDuration = c.code === 'duration';
        const isMoment = c.code === 'startedAt' || c.code === 'finishedAt';
        return {
          id: c.code,
          header: isDuration ? withSecondsLabel(c.title, timeDisplayFormat, tCommon('secondsSuffix')) : c.title,
          accessor: c.code as keyof QualityDetailItem,
          size: 160,
          cell: ({ value }) => {
            // Свойство, которого у разговора нет, отмечается прочерком — иначе пустую ячейку
            // не отличить от неполной выдачи
            if (value === null || value === undefined) return isDuration || isMoment ? '—' : null;
            if (isDuration) return formatTimeInterval(value as number, timeDisplayFormat);
            if (isMoment) return formatHumanDate(i18n, value as string, { withTime: true });
            return value as string | number;
          },
          ...(isSortableMain ? { enableSorting: true, enableFiltering: true, filter: { type: 'text' as const } } : {}),
        };
      });

    const optionalMain = mainFromApi.length > 0 ? mainFromApi : fallbackMain;

    // Атрибутные колонки — значения берутся из items[].attributes[code].
    // Сортировка и фильтрация не поддерживаются (IL: атрибутные колонки не сортируются).
    // Визуальное форматирование ячеек унифицировано с гридом диалогов:
    // CHECKBOX → галочка, INTEGER+SECOND → MM:SS/HH:MM:SS, PERCENT → суффикс %, и т.д.
    // Метаинформация (dataType/displayFormat) берётся из уже загруженной availableAttributes.
    const attributeByPath = new Map(availableAttributes.map((a) => [a.path, a]));
    const attributeCols: DataGridLeafColumn<QualityDetailItem>[] = availableColumns
      .filter((c) => c.type === 'attribute')
      .map((c) => {
        const attribute = attributeByPath.get(c.code);
        return {
          id: `attr_${c.code}`,
          header: withSecondsSuffix(c.title, attribute, timeDisplayFormat, tCommon('secondsSuffix')),
          accessor: (row) => row.attributes?.[c.code] ?? null,
          size: 160,
          cell: ({ value }) => renderQualityAttributeValue(value as string | null, attribute, timeDisplayFormat),
        };
      });

    // Колонки экспертных заключений (если бэк отдаёт). Без сортировки/фильтра.
    const expertReportCols: DataGridLeafColumn<QualityDetailItem>[] = availableColumns
      .filter((c) => c.type === 'expertReport')
      .map((c) => ({
        id: `er_${c.code}`,
        header: c.title,
        accessor: 'expertReportName',
        size: 160,
        cell: ({ value }) => (value as string) || null,
      }));

    // Критериальные колонки: столбец с результатом на каждый критерий выборки. Результат
    // показывается в том же виде, что и итог: балл — баллом, значение — значением, набор —
    // перечислением
    const criteriaCols = blockNames.map((n): DataGridLeafColumn<QualityDetailItem> => {
      const blockId = blockIdByName.get(n);
      const id = typeof blockId === 'number' ? `${BLOCK_COLUMN_PREFIX}${blockId}` : `block_name_${n}`;
      return {
        id,
        header: n,
        accessor: (row) => getQualityResultText(getBlockResult(row.blocks.find((b) => b.blockName === n))),
        size: 110,
        align: 'center',
        className: 'font-bold',
        cellClassName: ({ row }) => scoreClass(row.blocks.find((b) => b.blockName === n)?.rating ?? null),
        cell: ({ row }) => getQualityResultText(getBlockResult(row.blocks.find((b) => b.blockName === n))),
        ...(typeof blockId === 'number'
          ? { enableSorting: true, enableFiltering: true, filter: { type: 'integer' as const } }
          : {}),
      };
    });

    return [...mandatory, ...optionalMain, ...attributeCols, ...expertReportCols, ...criteriaCols];
  }, [t, tCommon, i18n, timeDisplayFormat, blockNames, availableColumns, availableAttributes, blockIdByName]);
};
